import { useEffect, useState } from 'react';
import './App.css';
import CompanyProfile from './components/CompanyProfile';
import { Profile, EngineInput, SaleParametersData, SalePhase, EngineOutput, Category, SaleProfile, SalePhaseData } from './model/model';
import SaleParameters from './components/SaleParameters';
import SalePhases from './components/SalePhases';
import styled, { css, keyframes } from 'styled-components';
import axios from 'axios';
import { convertToEngineOutput } from './helpers/convertToEngineOutput';
import Summary from './components/Summary';
import ErrorSnackbar from './components/subcomponents/ErrorSnackbar';
import { demoState } from './states/DemoState';
import { useRecoilValue } from 'recoil';
import { newComissionState } from './states/NewComissionState';
import { appVersionState } from './states/AppVersionState';

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const CalculateButton = styled.button<{ $loading: boolean, $isSmallScreen: boolean }>`
  width:  ${({ $isSmallScreen }) => ($isSmallScreen ? '80vw' : '64vw')};
  padding: 1rem 0rem;
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 500;
  background-size: 200% 200%;
  background-image: linear-gradient(to right, #3B56A6, #2081C4);
  color: #ffffff;
  border-radius: 30px;
  border: 2px solid #58585A;
  cursor: pointer;
  transition: none !important; 
  
  ${props => props.$loading && css`
    animation: ${gradientAnimation} 1s linear infinite;
  `};
`;

const CleanButtonContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CleanButtonTypography = styled.span`
  font-size: 1rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
`

const CleanButton = styled.button`
  border-radius: 50%;
  width: 60px; 
  height: 60px;
  padding: 0;
  border: 2px solid #58585A;
  background-image: linear-gradient(to right, #3B56A6, #2081C4);
  font-size: 2rem;
  color: #ffffff;
  cursor: pointer;
  transition: none !important; 
`;

const MobileMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const InputContainer = styled.div<{ $isSmallScreen: boolean }>`
width: ${({ $isSmallScreen }) => ($isSmallScreen ? '80vw' : '64vw')};
margin-bottom: 1rem;
`

const CompanyProfileContainer = styled.div`
margin-bottom: 1rem;
`;

const SaleDataContainer = styled.div`
display: flex;
flex-direction: row;
`;

const SaleParametersContainer = styled.div`
  width: 45%;
  padding-right: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const SalePhasesContainer = styled.div`
  width: 55%;
  padding-left: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const DemoContainer = styled.div`
  background-color: #fbd1d9;
  color: #721c24;
  padding: 20px;
  margin: 15px 0;
  border-radius: 5px;
  border: 1px solid #f5c6cb;
  text-align: center;
  font-size: 16px;

  a {
    color: #721c24; /* Same as text color for consistency */
    font-weight: bold;
    text-decoration: underline;
  }
`;

const NewComissionContainer = styled.div`
background-color: #6fbd71;
color: #134f14;
padding: 20px;
margin: 15px 0;
border-radius: 5px;
border: 1px solid #f5c6cb;
text-align: center;
font-size: 16px;

a {
  color: #134f14; /* Same as text color for consistency */
  font-weight: bold;
  text-decoration: underline;
}
`;


const defaultEngineInput: EngineInput = {
  profile: {
    sellersType: 0,
    vatPayer: true,
    companyType: 0,
    subscription: true,
  },
  saleParameters: {
    category: {} as Category,
    customerDeliveryFee: 0,
    packagingCosts: 0,
    productValue: 0,
    salesPercentageAllegroPay: 0,
    salesPercentageSmart: 0,
    vatValue: 0.23,
  },
  salePhases: [
    {
      uniqueId: `phase_${Date.now()} `,
      phaseNumber: 1,
      durationInDays: 0,
      soldItemsCount: 0,
      salePriceGross: 0,
      specialZoneMart: 0,
      isSpecialZoneMulti: false,
      highlight: 0,
      isPromoted: false,
      includesCoinsCost: false,
      allegroAdsBudgetGross: 0,
      shipmentPercentageBox: 0,
      shipmentPercentageCourier: 0,
      shipmentPercentagePoint: 0,
    }
  ]
}

const demoEngineInput: EngineInput = {
  profile: {
    sellersType: 0,
    vatPayer: true,
    companyType: 0,
    subscription: true,
  },
  saleParameters: {
    category: {
      segment: "Kalkulatory",
      hierarchy: "Firma i usługi > Biuro i Reklama > Sprzęt biurowy > Kalkulatory",
      comissionGroupOld: "C12",
      comissionGroupNew: "D17"
    },
    customerDeliveryFee: 3,
    packagingCosts: 3,
    productValue: 40,
    salesPercentageAllegroPay: 0.02,
    salesPercentageSmart: 0.7,
    vatValue: 0.23,
  },
  salePhases: [
    {
      uniqueId: `phase_${Date.now()} `,
      phaseNumber: 1,
      durationInDays: 60,
      soldItemsCount: 5,
      salePriceGross: 50,
      specialZoneMart: 0,
      isSpecialZoneMulti: false,
      highlight: 0,
      isPromoted: false,
      includesCoinsCost: false,
      allegroAdsBudgetGross: 0,
      shipmentPercentageBox: 0.3,
      shipmentPercentageCourier: 0.4,
      shipmentPercentagePoint: 0.3,
    }
  ]
}

function App() {
  const demo = useRecoilValue(demoState);
  const newComission = useRecoilValue(newComissionState);
  const appVersion = useRecoilValue(appVersionState);
  const cachedAppVersion = localStorage.getItem("appVersionState");
  const storedEngineInput = localStorage.getItem("engineInput");
  let usedEngineInput = demo ? demoEngineInput : storedEngineInput ? JSON.parse(storedEngineInput) : defaultEngineInput;
  if (appVersion !== cachedAppVersion) {
    localStorage.setItem("appVersionState", appVersion);
    usedEngineInput = demo ? demoEngineInput : defaultEngineInput;
  }
  const [engineInput, setEngineInput] = useState<EngineInput>(usedEngineInput);
  const [saleParametersData, setSaleParametersData] = useState<SaleParametersData>({
    category: usedEngineInput.saleParameters.category,
    customerDeliveryFee: usedEngineInput.saleParameters.customerDeliveryFee,
    packagingCosts: usedEngineInput.saleParameters.packagingCosts,
    productValue: usedEngineInput.saleParameters.productValue,
    salesPercentageAllegroPay: usedEngineInput.saleParameters.salesPercentageAllegroPay,
    salesPercentageSmart: usedEngineInput.saleParameters.salesPercentageSmart,
    vatValue: usedEngineInput.saleParameters.vatValue,
    shipmentPercentageBox: usedEngineInput.salePhases[0].shipmentPercentageBox,
    shipmentPercentageCourier: usedEngineInput.salePhases[0].shipmentPercentageCourier,
    shipmentPercentagePoint: usedEngineInput.salePhases[0].shipmentPercentagePoint,
  });
  const [salePhaseData, setSalePhaseData] = useState<SalePhaseData[]>(usedEngineInput.salePhases.map((phase: SalePhaseData) => ({
    uniqueId: phase.uniqueId,
    phaseNumber: phase.phaseNumber,
    durationInDays: phase.durationInDays,
    soldItemsCount: phase.soldItemsCount,
    salePriceGross: phase.salePriceGross,
    specialZoneMart: phase.specialZoneMart,
    isSpecialZoneMulti: phase.isSpecialZoneMulti,
    highlight: phase.highlight,
    isPromoted: phase.isPromoted,
    includesCoinsCost: phase.includesCoinsCost,
    allegroAdsBudgetGross: phase.allegroAdsBudgetGross,
  })));
  const [engineOutput, setEngineOutput] = useState<EngineOutput[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1600);

  useEffect(() => { //TODO: This looks super unoptimal
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 1600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleProfileChange = (profile: Profile) => {
    setEngineInput(prevState => ({
      ...prevState,
      profile
    }));
  }

  const handleSaleParametersChange = (saleParameters: SaleParametersData) => {
    setSaleParametersData(saleParameters);

    const saleProfile: SaleProfile = {
      category: saleParameters.category,
      customerDeliveryFee: saleParameters.customerDeliveryFee,
      packagingCosts: saleParameters.packagingCosts,
      productValue: saleParameters.productValue,
      salesPercentageAllegroPay: saleParameters.salesPercentageAllegroPay,
      salesPercentageSmart: saleParameters.salesPercentageSmart,
      vatValue: saleParameters.vatValue,
    };

    const salePhases: SalePhase[] = engineInput.salePhases.map(phase => ({
      uniqueId: phase.uniqueId,
      phaseNumber: phase.phaseNumber,
      durationInDays: phase.durationInDays,
      soldItemsCount: phase.soldItemsCount,
      salePriceGross: phase.salePriceGross,
      specialZoneMart: phase.specialZoneMart,
      isSpecialZoneMulti: phase.isSpecialZoneMulti,
      highlight: phase.highlight,
      isPromoted: phase.isPromoted,
      includesCoinsCost: phase.includesCoinsCost,
      allegroAdsBudgetGross: phase.allegroAdsBudgetGross,
      shipmentPercentageBox: saleParameters.shipmentPercentageBox,
      shipmentPercentageCourier: saleParameters.shipmentPercentageCourier,
      shipmentPercentagePoint: saleParameters.shipmentPercentagePoint,
    }));

    setEngineInput(prevState => ({
      ...prevState,
      saleParameters: saleProfile,
      salePhases: salePhases,
    }));
  }

  const handleSalePhasesChange = (salePhaseData: SalePhaseData[]) => {
    setSalePhaseData(salePhaseData);

    const salePhases: SalePhase[] = salePhaseData.map(phase => ({
      uniqueId: phase.uniqueId,
      phaseNumber: phase.phaseNumber,
      durationInDays: phase.durationInDays,
      soldItemsCount: phase.soldItemsCount,
      salePriceGross: phase.salePriceGross,
      specialZoneMart: phase.specialZoneMart,
      isSpecialZoneMulti: phase.isSpecialZoneMulti,
      highlight: phase.highlight,
      isPromoted: phase.isPromoted,
      includesCoinsCost: phase.includesCoinsCost,
      allegroAdsBudgetGross: phase.allegroAdsBudgetGross,
      shipmentPercentageBox: saleParametersData.shipmentPercentageBox,
      shipmentPercentageCourier: saleParametersData.shipmentPercentageCourier,
      shipmentPercentagePoint: saleParametersData.shipmentPercentagePoint,
    }));

    setEngineInput(prevState => ({
      ...prevState,
      salePhases
    }));
  }

  const handleCleanButtonClick = () => {
    localStorage.setItem("engineInput", JSON.stringify(demo ? demoEngineInput : defaultEngineInput));
    window.location.reload();
  };

  const sendEngineInput = async (engineInput: EngineInput) => {
    const requestBody = {
      seller_profile: {
        seller_type: engineInput.profile.sellersType,
        company_type: engineInput.profile.companyType,
        vat_payer: engineInput.profile.vatPayer,
        vat: engineInput.profile.vatPayer ? engineInput.saleParameters.vatValue : 0,
        allegro_subscription: engineInput.profile.subscription
      },
      sale_profile: {
        allegro_smart_percent: engineInput.saleParameters.salesPercentageSmart,
        allegro_pay_percent: engineInput.saleParameters.salesPercentageAllegroPay,
        allegro_base_commission: newComission ? engineInput.saleParameters.category.comissionGroupNew : engineInput.saleParameters.category.comissionGroupOld,
        client_delivery_cost_gross: engineInput.saleParameters.customerDeliveryFee,
        packaging_cost_gross: engineInput.saleParameters.packagingCosts,
        product_cost_gross: engineInput.saleParameters.productValue,
      },
      sale_phases: engineInput.salePhases.map(phase => ({
        phase_length_days: phase.durationInDays,
        phase_products_sold: phase.soldItemsCount,
        phase_auction_price_gross: phase.salePriceGross,
        phase_special_zone_mart: phase.specialZoneMart,
        phase_special_zone_multi: phase.isSpecialZoneMulti,
        phase_highlight: phase.highlight,
        phase_promote: phase.isPromoted,
        phase_coins: phase.includesCoinsCost,
        phase_allegro_ads_budget_gross: phase.allegroAdsBudgetGross,
        percentage_box: phase.shipmentPercentageBox,
        percentage_courier: phase.shipmentPercentageCourier,
        percentage_point: phase.shipmentPercentagePoint
      }))
    };
    const apiUrl = 'https://allenarzedzie.pl/wp-json/allecalculator/v1/engine';
    try {
      const response = await axios.post(apiUrl, requestBody);
      setEngineOutput(response.data.map((item: any) => { return convertToEngineOutput(item) }));
      setLoading(false);
    } catch (error) {
      console.error('Error sending engine input:', error);
      setError('Wystąpił błąd podczas obliczania wyników. Spróbuj ponownie później.');
    }
  };

  useEffect(() => {
    if (engineInput !== defaultEngineInput && !demo) {
      localStorage.setItem("engineInput", JSON.stringify(engineInput));
    }
  }, [engineInput])

  useEffect(() => {
    if (loading) {
      let errorMessage = '';

      if (engineInput.saleParameters.category.segment === undefined) {
        errorMessage += "Nie wybrano kategorii\n";
      }

      if (engineInput.salePhases[0].shipmentPercentageBox + engineInput.salePhases[0].shipmentPercentageCourier + engineInput.salePhases[0].shipmentPercentagePoint !== 1) {
        errorMessage += "Suma procentów wysyłki musi wynosić 100%\n";
      }

      engineInput.salePhases.forEach(phase => {
        const durationInDaysEmptyError = phase.durationInDays === 0 ? "Nie wybrano długości fazy" : "";
        const durationInDaysLimitError = phase.durationInDays > 60 ? "Długość fazy nie może przekraczać 60 dni" : "";
        const soldItemsCountEmptyError = phase.soldItemsCount === 0 ? "Nie wybrano liczby sprzedanych przedmiotów" : "";
        const salePriceGrossEmptyError = phase.salePriceGross === 0 ? "Nie wybrano ceny sprzedaży" : "";

        if (durationInDaysEmptyError || durationInDaysLimitError || soldItemsCountEmptyError || salePriceGrossEmptyError) {
          errorMessage += `Faza ${phase.phaseNumber}: ${durationInDaysEmptyError}${durationInDaysLimitError}${soldItemsCountEmptyError}${salePriceGrossEmptyError} \n`;
        }
      });

      if (errorMessage) {
        setError(errorMessage);
        setLoading(false);
        return;
      }

      sendEngineInput(engineInput);
    }
  }, [loading])

  return (
    <>

      {isMobile ? (
        <MobileMessageContainer>
          <h5>Wersja mobilna już wkrótce!</h5>
          <img src="https://allenarzedzie.pl/wp-content/mobile-ph.png" alt="mobile-ph.png" className='mobile-ph' />
        </MobileMessageContainer>
      ) : (
        <div className="app">
          {error && <ErrorSnackbar message={error} onClose={() => setError(null)} />}
          <InputContainer $isSmallScreen={isSmallScreen}>
            {demo &&
              <DemoContainer>
                Przeglądasz bezpłatną wersję demo kalkulatora. Pola zaznaczone na czerwono można odblokować do edycji po zakupie subskrypcji na stronie: <a href="https://allenarzedzie.pl/subskrypcja" target="_blank" rel="noopener noreferrer">allenarzedzie.pl/subskrypcja</a>.<b /> Jeżeli masz jakiekolwiek pytania, zapraszamy do kontaktu poprzez emaila (pomoc@allenarzedzie.pl) lub czat umieszczony w lewym dolnym rogu.
              </DemoContainer>
            }
            <NewComissionContainer>
              Kalkulator uwzględnia wszystkie zmiany wprowadzone do regulaminu Allegro w lutym 2024 roku, w tym zmiany do systemu prowizyjnego oraz nowe dopłaty do Allegro SMART.
            </NewComissionContainer>
            <CompanyProfileContainer>
              <CompanyProfile profile={engineInput.profile} onProfileChange={handleProfileChange} />
            </CompanyProfileContainer>
            <SaleDataContainer>
              <SaleParametersContainer>
                <SaleParameters saleParameters={saleParametersData} onSaleParametersChange={handleSaleParametersChange} />
              </SaleParametersContainer>
              <SalePhasesContainer>
                <SalePhases salePhases={salePhaseData} onSalePhasesChange={handleSalePhasesChange} />
              </SalePhasesContainer>
            </SaleDataContainer>
          </InputContainer>
          <CalculateButton $isSmallScreen={isSmallScreen} $loading={loading} onClick={(e) => { setLoading(true); setError(null) }}>OBLICZ</CalculateButton>
          <CleanButtonContainer>
            <CleanButtonTypography>WYCZYŚĆ</CleanButtonTypography>
            <CleanButton onClick={handleCleanButtonClick}>↻</CleanButton>
          </CleanButtonContainer>
          {engineOutput && <Summary engineOutput={engineOutput} isSmallScreen={isSmallScreen} />}
        </div>
      )}
    </>
  );
}

export default App;
